'use client';

import { useEffect, useCallback, useRef, useState } from 'react';
import { VideoJobResponse } from '@/lib/types';
import api from '@/lib/api';

interface VideoModalProps {
  video: VideoJobResponse;
  videos: VideoJobResponse[];
  onClose: () => void;
  onNavigate: (video: VideoJobResponse) => void;
  onViewSourceImage?: (jobId: string) => void;
}

export default function VideoModal({
  video,
  videos,
  onClose,
  onNavigate,
  onViewSourceImage
}: VideoModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  // Find current index and adjacent videos
  const currentIndex = videos.findIndex(v => v.job_id === video.job_id);
  const prevVideo = currentIndex > 0 ? videos[currentIndex - 1] : null;
  const nextVideo = currentIndex < videos.length - 1 ? videos[currentIndex + 1] : null;

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && prevVideo) {
      onNavigate(prevVideo);
    } else if (e.key === 'ArrowRight' && nextVideo) {
      onNavigate(nextVideo);
    } else if (e.key === ' ') {
      e.preventDefault();
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setIsPlaying(true);
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }
    }
  }, [onClose, onNavigate, prevVideo, nextVideo]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Auto-play video when modal opens
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [video.job_id]);

  if (video.status !== 'completed' || !video.video_filename) {
    return null;
  }

  const duration = (video.frames / 24).toFixed(1);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 animate-fadeIn flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white/40 text-sm">
            {currentIndex + 1} / {videos.length}
          </span>
          {/* Video badge */}
          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded">
            Video
          </span>
        </div>
        <p className="text-white/80 text-sm truncate max-w-xl">{video.prompt}</p>
        <div className="flex items-center gap-2">
          {video.source_job_id && onViewSourceImage && (
            <button
              onClick={() => onViewSourceImage(video.source_job_id!)}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
              title="View Source Image"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </button>
          )}
          <a
            href={api.getVideoUrl(video.video_filename)}
            download
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Download"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
        </div>
      </div>

      {/* Main video area with navigation */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {prevVideo && (
          <button
            onClick={() => onNavigate(prevVideo)}
            className="absolute left-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
            title="Previous (Left Arrow)"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}

        {/* Video */}
        <div className="max-w-[90vw] max-h-[calc(100vh-14rem)] relative">
          <video
            ref={videoRef}
            src={api.getVideoUrl(video.video_filename)}
            className="max-w-full max-h-[calc(100vh-14rem)] object-contain cursor-pointer"
            loop
            playsInline
            onClick={togglePlayPause}
          />
          {/* Play/Pause overlay */}
          {!isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
              onClick={togglePlayPause}
            >
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Next button */}
        {nextVideo && (
          <button
            onClick={() => onNavigate(nextVideo)}
            className="absolute right-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
            title="Next (Right Arrow)"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom bar with details */}
      <div className="bg-black/50 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-white/40">Size </span>
                <span className="text-white/80">{video.width}x{video.height}</span>
              </div>
              <div>
                <span className="text-white/40">Frames </span>
                <span className="text-white/80">{video.frames}</span>
              </div>
              <div>
                <span className="text-white/40">Duration </span>
                <span className="text-white/80">{duration}s</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-white/60">
              <span>Space to play/pause</span>
              <span>Arrow keys to navigate</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
